// ═══════════════════════════════════════════════════════════════════════════════
// Feature Visibility — controls which nav sections each role can see.
// Hot-swappable: edit the map below to grant/restrict sections or individual items.
//
//   true         → show every item in the section
//   ['id', ...]  → show only these specific view IDs from the section
//   (omitted)    → section is hidden entirely
//
// Only known admin/lead roles are unrestricted. Unknown roles/views are denied.
// ═══════════════════════════════════════════════════════════════════════════════
import type { UserRole, ViewId } from '../types';
import { ALL_VIEWS } from './allViews';

/** true = whole section visible; string[] = only these viewIds within the section */
type SectionAccess = true | string[];

export const ROLE_VISIBLE_SECTIONS: Partial<Record<UserRole, Record<string, SectionAccess>>> = {
  analyst: {
    'KNOWLEDGE BASE': true,
    'SOC OPERATIONS': ['handoff', 'customers', 'checklist'],
    // Analysts can read their own QA scores + reports; the qa_admin surface
    // stays admin-only via its sidebar entry under ADMIN.
    'PEOPLE & QUALITY': ['qa'],
    // Analysts get ONLY their own engineering-request tracker — not the
    // full engineer queue or threat hunting. The Engineering tab itself
    // remains gated to canEdit('engineering').
    'TIER THREE': ['my-engineering-requests'],
    // Training is open to every staff role. Analysts are the people the courses
    // are FOR, and hiding the section was the only thing keeping them out: the
    // learner-facing training endpoints already gate on requireStaff (which an
    // analyst passes), and the management surfaces - assignments, groups,
    // everyone's progress, compliance, SCORM upload - gate separately on
    // requireViewAccess('lms', 'edit'|'delete'), which an analyst does not have.
    // So this reveals a surface analysts were already authorised to call; it
    // grants no new API capability.
    //
    // Guests are unaffected. Customer-role users are pinned to CUSTOMER PORTAL
    // by their own map below, and requireStaff rejects the customer role and
    // confirmed external guests outright.
    'TRAINING': true,
    'SETTINGS': ['my-feature-requests'],
    // Uncomment / add sections below to enable more for analysts:
    // 'PROJECTS': true,
    // 'REPORTING': true,
  },
  // Customer-role users (external guests) get ONLY their portal. This map
  // stays explicit; customer navigation is never inferred from a missing map.
  customer: {
    'CUSTOMER PORTAL': ['customerPortal'],
  },
  // Known lead/admin roles are handled explicitly below.
};

/** Maps each view ID to the section it belongs to. Used to guard direct navigation. */
const VIEW_TO_SECTION: Record<string, string> = {
  home: 'KNOWLEDGE BASE', kb: 'KNOWLEDGE BASE', article: 'KNOWLEDGE BASE',
  editor: 'KNOWLEDGE BASE', kql: 'KNOWLEDGE BASE', verify: 'KNOWLEDGE BASE',
  mitre: 'KNOWLEDGE BASE', decisions: 'KNOWLEDGE BASE', ai: 'KNOWLEDGE BASE', glossary: 'KNOWLEDGE BASE',
  handoff: 'SOC OPERATIONS', customers: 'SOC OPERATIONS', tickets: 'SOC OPERATIONS',
  ticket: 'SOC OPERATIONS', freshservice: 'SOC OPERATIONS',
  sla: 'SOC OPERATIONS', escalations: 'SOC OPERATIONS',
  roster: 'SOC OPERATIONS', checklist: 'SOC OPERATIONS',
  // IC coverage heat map — deliberately NOT in the analyst SOC OPERATIONS
  // whitelist: analysts confirm coverage inside the Shift Handoff flow; the
  // heat map is a command surface (lead/admin + the Incident Commander
  // custom RBAC role via a canView grant).
  coverage: 'SOC OPERATIONS',
  // Customer-facing portal — its own section so customer-role users see a
  // single clean nav group; staff (lead/admin) see it too for preview.
  customerPortal: 'CUSTOMER PORTAL',
  ir: 'TIER THREE',
  threatHunting: 'TIER THREE',
  engineering: 'TIER THREE',
  // Cross-tenant provisioning requests — engineer-only. RBAC is aliased to
  // `engineering` (see RBAC_VIEW_ALIAS); analysts are NOT in the TIER THREE
  // whitelist for it, so only engineers (engineering canView/canEdit) and
  // lead/admin see it.
  provisioning: 'TIER THREE',
  // Customer onboarding — writes to Patriot's central Sentinel workspace, so it
  // lives in TIER THREE and is deliberately NOT in the analyst whitelist for
  // that section: lead/admin see it as unrestricted roles; analysts need an
  // explicit custom-role canView grant (the "Onboarding role"). Customers can
  // never see it (their section map contains only CUSTOMER PORTAL, and
  // App.tsx hard-pins them there).
  onboarding: 'TIER THREE',
  // Analyst-facing "My Engineering Requests". RBAC is aliased to `engineering`
  // (see RBAC_VIEW_ALIAS) so the matrix toggle on Engineering controls
  // canEdit (submit-from-page). The sidebar visibility for analysts is
  // granted explicitly below in ROLE_VISIBLE_SECTIONS.
  'my-engineering-requests': 'TIER THREE',
  qa: 'PEOPLE & QUALITY', 'roster-mgmt': 'PEOPLE & QUALITY',
  qa_admin: 'ADMIN',
  lms: 'TRAINING',
  projects: 'PROJECTS', tasks: 'PROJECTS', mytasks: 'PROJECTS',
  calendar: 'PROJECTS',
  workload: 'PROJECTS',
  reports: 'REPORTING',
  // General Management — GM-tier surface (RMS→Hub integration, Phase 1).
  // Hidden from restricted roles (analyst/customer) by omission from their
  // ROLE_VISIBLE_SECTIONS maps; lead/admin see it as unrestricted roles, and
  // GM-tier users are force-granted via isGmView() (sidebar + navigate()).
  gm: 'GENERAL MANAGEMENT', traction: 'GENERAL MANAGEMENT', hiring: 'GENERAL MANAGEMENT',
  people: 'GENERAL MANAGEMENT', discipline: 'GENERAL MANAGEMENT', 'exec-reports': 'GENERAL MANAGEMENT',
  admin: 'ADMIN', users: 'ADMIN', 'signin-log': 'ADMIN',
  api_keys: 'SETTINGS',
  'my-feature-requests': 'SETTINGS',
};

export function isKnownUserRole(role: unknown): role is UserRole {
  return role === 'admin' || role === 'lead' || role === 'analyst' || role === 'customer';
}

export function isKnownView(view: string): boolean {
  return Object.prototype.hasOwnProperty.call(VIEW_TO_SECTION, view);
}

/** Any view built-in admin/lead navigation may reach. Not a custom-grant check. */
export function isKnownNavigationView(view: string): boolean {
  return isKnownView(view) || ALL_VIEWS.some(row => row.id === view);
}

/**
 * A view a custom RBAC row can grant: the canonical editor matrix, reached
 * directly or through a defined alias. This mirrors the ids the role API will
 * store (RBAC_PERMISSION_VIEW_IDS, parity-tested against ALL_VIEWS), so the UI
 * never honors a custom row the server-side role validator would refuse.
 */
export function isKnownPermissionView(view: string): boolean {
  const key = rbacViewKey(view);
  return ALL_VIEWS.some(row => row.id === key);
}

const PERMISSION_FLAGS = ['canView', 'canEdit', 'canDelete'] as const;

/**
 * For the role editor: give a stored row any MISSING boolean flag as false.
 * A missing flag was already no grant under every check, so this changes no
 * effective permission; it lets rows saved before a flag existed round-trip
 * through the editor. Present non-boolean values are deliberately left as-is
 * so they are never silently re-interpreted; the role API rejects them.
 */
export function completeMissingPermissionFlags<T>(row: T): T {
  if (!row || typeof row !== 'object' || Array.isArray(row) || Object.getPrototypeOf(row) !== Object.prototype) return row;
  const r = row as unknown as Record<string, unknown>;
  const own = (k: string) => Object.prototype.hasOwnProperty.call(r, k);
  if (!PERMISSION_FLAGS.every(f => !own(f) || typeof r[f] === 'boolean')) return row;
  if (PERMISSION_FLAGS.every(own)) return row;
  const out: Record<string, unknown> = { ...r };
  for (const f of PERMISSION_FLAGS) if (!own(f)) out[f] = false;
  return out as unknown as T;
}

/**
 * For the role editor: stored rows the matrix cannot show - any entry whose
 * viewId is not a canonical ALL_VIEWS id, plus non-object entries. The editor
 * would drop these on save, silently removing a grant, so it refuses to save a
 * role that still holds any of them. Absent (undefined/null) permissions hold
 * nothing to lose; any other non-array value is itself reported as retired.
 */
export function retiredPermissionRows(rows: unknown): unknown[] {
  if (rows === undefined || rows === null) return [];
  if (!Array.isArray(rows)) return [rows];
  return rows.filter(row => {
    if (!row || typeof row !== 'object' || Array.isArray(row)) return true;
    const viewId = (row as Record<string, unknown>).viewId;
    return typeof viewId !== 'string' || !ALL_VIEWS.some(v => v.id === viewId);
  });
}

/**
 * For the role editor and role list: every reason a stored role's permissions
 * cannot be shown faithfully in the one-row-per-view matrix. Empty means safe.
 * The editor refuses to open or save a role with any problem, so nothing is
 * normalized, merged or deleted. Only an absent (undefined) value is treated
 * as "no stored rows"; null and any other non-array are refused.
 *   - not an array
 *   - an entry that is not a plain object, or whose viewId is not a string
 *   - a viewId that is not a canonical ALL_VIEWS id (retired)
 *   - the same canonical viewId stored more than once (the matrix keeps one
 *     row per view, so saving would silently drop the others)
 *   - a present flag, label or knowledge-base scope with an invalid type
 */
export function storedPermissionProblems(rows: unknown): string[] {
  if (rows === undefined) return [];
  if (!Array.isArray(rows)) return ['stored permissions are not a list'];
  const problems: string[] = [];
  const seen = new Set<string>();
  const reported = new Set<string>();
  for (const row of rows) {
    if (!row || typeof row !== 'object' || Array.isArray(row)) { problems.push('unreadable entry'); continue; }
    const viewId = (row as Record<string, unknown>).viewId;
    if (typeof viewId !== 'string') { problems.push('unreadable entry'); continue; }
    if (!ALL_VIEWS.some(v => v.id === viewId)) { problems.push(`retired: ${viewId}`); continue; }
    if (seen.has(viewId)) {
      if (!reported.has(viewId)) { problems.push(`duplicate: ${viewId}`); reported.add(viewId); }
      continue;
    }
    seen.add(viewId);
    const permission = row as Record<string, unknown>;
    const own = (key: string) => Object.prototype.hasOwnProperty.call(permission, key);
    for (const flag of PERMISSION_FLAGS) {
      if (own(flag) && typeof permission[flag] !== 'boolean') problems.push(`non-boolean flag: ${viewId}.${flag}`);
    }
    if (own('label') && typeof permission.label !== 'string') problems.push(`invalid label: ${viewId}`);
    if (own('knowledgeBaseIds') && (!Array.isArray(permission.knowledgeBaseIds)
      || !permission.knowledgeBaseIds.every(id => typeof id === 'string' && id.trim().length > 0))) {
      problems.push(`invalid KB scope: ${viewId}`);
    }
  }
  return problems;
}

/** Role-list summary that never throws on malformed stored permissions. */
export function rolePermissionSummary(rows: unknown, total: number): string {
  if (storedPermissionProblems(rows).length) return 'Stored permissions need administrator review';
  const list = Array.isArray(rows) ? rows : [];
  return `${list.filter(p => (p as Record<string, unknown>).canView === true).length}/${total} views accessible`;
}

export function hasLiteralPermission(row: unknown, viewId: string, field: string): boolean {
  if (!row || typeof row !== 'object' || Array.isArray(row)) return false;
  const permission = row as Record<string, unknown>;
  return Object.prototype.hasOwnProperty.call(permission, 'viewId') && permission.viewId === viewId
    && Object.prototype.hasOwnProperty.call(permission, field) && permission[field] === true;
}

/**
 * True if the view belongs to the GENERAL MANAGEMENT section. GM-tier users
 * always get these views regardless of base-role feature visibility — checked
 * by the Sidebar item filter and the navigate() guard in useAppState.
 */
export function isGmView(viewId: string): boolean {
  return isKnownView(viewId) && VIEW_TO_SECTION[viewId] === 'GENERAL MANAGEMENT';
}

/** Returns true if the given role is allowed to access the view. */
export function isViewAllowed(role: UserRole, view: ViewId): boolean {
  if (!isKnownUserRole(role) || !isKnownView(view)) return false;
  if (role === 'admin' || role === 'lead') return true;
  const roleConfig = ROLE_VISIBLE_SECTIONS[role];
  if (!roleConfig) return false;
  const section = VIEW_TO_SECTION[view];
  const access = Object.prototype.hasOwnProperty.call(roleConfig, section) ? roleConfig[section] : undefined;
  if (!access) return false;  // section not listed → hidden
  if (access === true) return true; // whole section visible
  return access.includes(view); // only specific items
}

/**
 * Filters nav items for a given section based on the role's config.
 * Returns null if the entire section is hidden, or the subset of allowed item IDs.
 */
export function getAllowedItemIds(role: UserRole, sectionTitle: string, itemIds: string[]): string[] | null {
  if (!isKnownUserRole(role)) return null;
  const knownItems = itemIds.filter(id => isKnownView(id) && VIEW_TO_SECTION[id] === sectionTitle);
  if (role === 'admin' || role === 'lead') return knownItems;
  const roleConfig = ROLE_VISIBLE_SECTIONS[role];
  if (!roleConfig) return null;
  const access = Object.prototype.hasOwnProperty.call(roleConfig, sectionTitle) ? roleConfig[sectionTitle] : undefined;
  if (!access) return null; // section hidden
  if (access === true) return knownItems;
  return knownItems.filter(id => access.includes(id));
}

// ═══════════════════════════════════════════════════════════════════════════════
// RBAC VIEW ALIASING — keep the permission matrix manageable
// ═══════════════════════════════════════════════════════════════════════════════
// Several views share a single RBAC row (umbrella permission) so admins don't
// have to toggle ten checkboxes for one logical capability. When custom-role
// permissions are checked, the caller's viewId is aliased through this map
// first, so e.g. asking for `workload` actually consults the `pm-advanced`
// permission. The list in `AdminPage.tsx` ALL_VIEWS only contains the
// CANONICAL permission ids (projects, pm-advanced, …) — not the aliased ones.
//
// Today's groupings:
//   • `projects`     gates Projects + Calendar  (daily)
//   • `pm-advanced`  gates Workload             (mgmt)
//   • `tasks` keeps its own dedicated row (existing precedent)
//     The legacy `mytasks` viewId aliases to `tasks` so old bookmarks keep working.
//
// To add a NEW grouping: add the alias here AND a row in `ALL_VIEWS`. To split
// a grouping: remove the alias and add per-view rows.
export const RBAC_VIEW_ALIAS: Record<string, string> = {
  // The own-request page is a known view, not an unknown-view fallback.
  'my-feature-requests': 'feature_requests',
  // Daily-driver PM views inherit from the base "Projects" permission
  calendar: 'projects',
  // Management-tier PM views require the "Project Management (advanced)" permission
  workload: 'pm-advanced',
  // "My Engineering Requests" inherits canEdit/canDelete from the Engineering
  // permission. The matrix already has an `engineering` row — no new row
  // needed; canEdit there controls the in-page "+ New request" button.
  // Analyst sidebar visibility for THIS specific id is granted via
  // ROLE_VISIBLE_SECTIONS above (analysts cannot see Engineering itself).
  'my-engineering-requests': 'engineering',
  // Cross-tenant provisioning is a facet of the Engineering capability — it
  // inherits the `engineering` permission's canView/canEdit rather than getting
  // its own matrix row (per the product decision that provisioning need not be
  // separated from engineering). canEdit there gates submit; canView gates the
  // sidebar item + page for custom (engineer) roles.
  provisioning: 'engineering',
  // Legacy viewId — the page was renamed from "My Tasks" to "Tasks" (global
  // tasks view with filters). Old bookmarks (v=mytasks) and surfaces that
  // still reference the legacy id resolve through the canonical 'tasks' row.
  mytasks: 'tasks',
  // Customer Portal inherits the Client Management umbrella permission — it is
  // another facet of the same customer-data capability (read-only, scoped
  // server-side). Customer-role visibility comes from ROLE_VISIBLE_SECTIONS,
  // not from this alias.
  customerPortal: 'customers',
};

/** Resolve a view id to the RBAC permission key that gates it. */
export function rbacViewKey(viewId: string): string {
  return Object.prototype.hasOwnProperty.call(RBAC_VIEW_ALIAS, viewId) ? RBAC_VIEW_ALIAS[viewId] : viewId;
}
